import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import sanity from "../../../utils/sanity";
import styled from "@emotion/styled";
import { COLORS } from "../../../utils/styles/colors";
import { BREAKPOINTS } from "../../../utils/styles/breakpoints";
import { useMediaMatch } from "rooks";
import {
  ComponentLStyling,
  ComponentSStyling,
  TextStyle,
} from "../../../utils/styles/typography";
import { useTranslation } from "react-i18next";

// === Styled Components ===

const Background = styled.div`
  background-color: ${COLORS.CustomGreen};
  padding: 16px 16px;
  border-bottom: solid 2px ${COLORS.CustomSkin};

  ${BREAKPOINTS.MEDIUM} {
    padding: 28px 32px;
  }

  ${BREAKPOINTS.XLARGE} {
    padding: 28px 56px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  padding: 24px 0;
  max-width: 1200px;
  margin: 0 auto;
`;

type FormData = {
  fullName: string;
  email: string;
  mobileNumber: string;
  message: string;
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 400px;
  margin: 20px auto;
  padding: 2rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 19px 38px,
    rgba(0, 0, 0, 0.22) 0px 15px 12px;
`;

const CallOrder = styled.div`
  ${ComponentLStyling(TextStyle.SemiBold, COLORS.White)}
`;

const Label = styled.label`
  ${ComponentSStyling(TextStyle.Regular, COLORS.Black)}
  margin-bottom: 0.5rem;
  width: 100%;
`;

const Input = styled.input`
  ${ComponentSStyling(TextStyle.Regular, COLORS.Black)}
  margin-top: 5px;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 92%;

  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: ${COLORS.CustomSkin};
  ${ComponentLStyling(TextStyle.SemiBold, COLORS.Black)}
  font-size: 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${COLORS.CustomSkin1};
  }
`;

const Textarea = styled.textarea`
  ${ComponentSStyling(TextStyle.Regular, COLORS.Black)}
  margin-top: 5px;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 92%;
  resize: vertical;
  height: 100px;
  max-height: 200px;
  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
  }
`;

const SuccessMessage = styled.div`
  ${ComponentLStyling(TextStyle.SemiBold, COLORS.Positive600)}
  font-size: 1rem;
  margin-top: 1rem;
  text-align: center;
`;

// === Component ===

const UserForm: React.FC = () => {
  const { t } = useTranslation();
  const { register, handleSubmit, reset } = useForm<FormData>();
  const [success, setSuccess] = React.useState<boolean>(false);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      await sanity.createContact(data);
      setSuccess(true);
      reset();
    } catch (error) {
      console.error("Failed to submit:", error);
    }
  };
  const CharCount = styled.div`
    text-align: right;
    font-size: 0.85rem;
    color: ${COLORS.Neutral300};
  `;
  const [messageValue, setMessageValue] = React.useState("");
  return (
    <Background>
      <Wrapper>
        <CallOrder>{t("CallOrderTitle", "შეუკვეთე ზარი")}</CallOrder>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Label>{t("FullNameLabel", "სრული სახელი:")}</Label>
            <Input
              {...register("fullName", { required: true })}
              placeholder={t("FullNamePlaceholder", "შეიყვანეთ სრული სახელი")}
            />
          </div>
          <div>
            <Label>{t("EmailLabel", "Email:")}</Label>
            <Input
              {...register("email", { required: true })}
              type="email"
              placeholder={t(
                "EmailPlaceholder",
                "შეიყვანეთ ელ. ფოსტა მისამართი"
              )}
            />
          </div>
          <div>
            <Label>{t("PhoneLabel", "ტელეფონის ნომერი:")}</Label>
            <Input
              {...register("mobileNumber", { required: true })}
              type="tel"
              placeholder={t("PhonePlaceholder", "შეიყვანეთ ტელეფონის ნომერი")}
            />
          </div>
          <div>
            <Label>{t("MessageLabel", "რით შეგვიძლია დაგეხმაროთ?")}</Label>
            <Textarea
              {...register("message", {
                required: false,
                onChange: (e) => setMessageValue(e.target.value),
              })}
              placeholder={t("MessagePlaceholder", "დატოვეთ შეტყობინება აქ")}
              maxLength={250}
              rows={4}
            />
            <CharCount>{messageValue.length} / 250</CharCount>
          </div>
          <Button type="submit">{t("SubmitButton", "გამოგვიგზავნეთ")}</Button>
          {success && (
            <SuccessMessage>
              {t(
                "SuccessMessage",
                "საკონტაქტო ინფორმაცია წარმატებით გაიგზავნა!"
              )}
            </SuccessMessage>
          )}
        </Form>
      </Wrapper>
    </Background>
  );
};

export default UserForm;
